import React, { useEffect, useState, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableRow, TableCell, TableBody,
  Checkbox, Button, TextField, Grid, IconButton, CircularProgress,
  Accordion, AccordionSummary, AccordionDetails, Typography, Chip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { ProductApi } from '../../api/productApi';

const clean = (s) => (s || '').toString().trim();

const extractVariantLabel = (name) => {
  const parts = clean(name).split(' ').filter(Boolean);
  if (parts.length <= 1) return clean(name);
  return parts[parts.length - 1];
};

const extractBaseName = (name) => {
  const parts = clean(name).split(' ').filter(Boolean);
  if (parts.length <= 1) return clean(name);
  parts.pop();
  return parts.join(' ');
};

const extractSkuGroup = (sku) => {
  const s = clean(sku);
  if (!s) return '';
  const parts = s.split('-');
  if (parts.length >= 2) return `${parts[0]}-${parts[1]}`;
  return s;
};

const buildGroups = (items = []) => {
  const map = new Map();

  items.forEach((it) => {
    const sku = clean(it?.sku);
    const name = clean(it?.name);

    const baseName = extractBaseName(name);
    const variantLabel = extractVariantLabel(name);
    const skuGroup = extractSkuGroup(sku);

    const key = `${baseName}__${skuGroup}`;

    if (!map.has(key)) {
      map.set(key, {
        key,
        title: baseName || name || sku || 'Item',
        skuGroup,
        currency: it?.currency,
        variants: []
      });
    }

    map.get(key).variants.push({
      ...it,
      _baseName: baseName,
      _variantLabel: variantLabel,
      _skuGroup: skuGroup
    });
  });

  const groups = Array.from(map.values());

  groups.forEach((g) => {
    g.variants = (g.variants || []).sort((a, b) => {
      const an = clean(a?._variantLabel).toLowerCase();
      const bn = clean(b?._variantLabel).toLowerCase();
      if (an < bn) return -1;
      if (an > bn) return 1;
      return 0;
    });
  });

  return groups.sort((a, b) => {
    const at = clean(a.title).toLowerCase();
    const bt = clean(b.title).toLowerCase();
    if (at < bt) return -1;
    if (at > bt) return 1;
    return 0;
  });
};

const OnDemandCatalogueModal = ({ isOpen, onClose, storeId, mode = 'product', onConfirm, disabledSkus = [] }) => {
  const [page, setPage] = useState(1);
  const [count] = useState(20);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSkus, setSelectedSkus] = useState([]);
  const [q, setQ] = useState('');
  const [expanded, setExpanded] = useState({});
  const [cachedDisabledSkus, setCachedDisabledSkus] = useState([]);
  const singleMode = mode === 'product';

  useEffect(() => {
    if (!isOpen) return;
    setSelectedSkus([]);
    setQ('');
    setExpanded({});
    setPage(1);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const list = (disabledSkus || []).map((s) => clean(s)).filter(Boolean);
    if (list.length) setCachedDisabledSkus(list);
  }, [isOpen, disabledSkus]);

  const disabledSet = useMemo(() => {
    return new Set((cachedDisabledSkus || []).map((s) => clean(s)).filter(Boolean));
  }, [cachedDisabledSkus]);

  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      setLoading(true);
      try {
        const res = await ProductApi.getOnDemandCatalogue({ storeId, page, count });
        setRows(res?.data?.items || []);
      } finally {
        setLoading(false);
      }
    })();
  }, [isOpen, page, count, storeId]);

  const groups = useMemo(() => buildGroups(rows), [rows]);

  const filteredGroups = useMemo(() => {
    const term = clean(q).toLowerCase();
    if (!term) return groups;

    return groups
      .map((g) => {
        const titleHit = clean(g.title).toLowerCase().includes(term);
        const skuHit = clean(g.skuGroup).toLowerCase().includes(term);
        const v = (g.variants || []).filter((it) => {
          const nameHit = clean(it.name).toLowerCase().includes(term);
          const skuH = clean(it.sku).toLowerCase().includes(term);
          const labelHit = clean(it._variantLabel).toLowerCase().includes(term);
          return nameHit || skuH || labelHit;
        });

        if (titleHit || skuHit) return { ...g, variants: g.variants };
        if (v.length) return { ...g, variants: v };
        return null;
      })
      .filter(Boolean);
  }, [groups, q]);

  const isDisabledSku = (sku) => disabledSet.has(clean(sku));

  const toggleSku = (sku) => {
    if (!sku) return;
    if (isDisabledSku(sku)) return;
    if (singleMode) {
      setSelectedSkus([sku]);
    } else {
      setSelectedSkus((prev) => prev.includes(sku) ? prev.filter((s) => s !== sku) : [...prev, sku]);
    }
  };

  const groupEnabledSkus = (g) => (g.variants || []).map((x) => x.sku).filter((s) => s && !isDisabledSku(s));
  const groupAllSkus = (g) => (g.variants || []).map((x) => x.sku).filter(Boolean);

  const isGroupAllSelected = (g) => {
    const skus = groupEnabledSkus(g);
    if (!skus.length) return false;
    return skus.every((s) => selectedSkus.includes(s));
  };

  const isGroupSomeSelected = (g) => {
    const skus = groupEnabledSkus(g);
    if (!skus.length) return false;
    const any = skus.some((s) => selectedSkus.includes(s));
    const all = skus.every((s) => selectedSkus.includes(s));
    return any && !all;
  };

  const isGroupFullyDisabled = (g) => {
    const all = groupAllSkus(g);
    if (!all.length) return false;
    return all.every((s) => isDisabledSku(s));
  };

  const toggleGroup = (g) => {
    if (singleMode) return;
    if (isGroupFullyDisabled(g)) return;
    const skus = groupEnabledSkus(g);
    if (!skus.length) return;

    const allSelected = skus.every((s) => selectedSkus.includes(s));
    if (allSelected) {
      setSelectedSkus((prev) => prev.filter((s) => !skus.includes(s)));
    } else {
      setSelectedSkus((prev) => Array.from(new Set([...prev, ...skus])));
    }
  };

  const handleConfirm = () => {
    const selected = rows
      .filter((r) => selectedSkus.includes(r.sku))
      .filter((r) => !isDisabledSku(r.sku));
    onConfirm(selected);
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="lg" fullWidth>
      <Grid container alignItems="center" justifyContent="space-between" sx={{ px: 2, pt: 2 }}>
        <DialogTitle sx={{ m: 0, p: 0 }}>
          {singleMode ? 'Import Product from On-Demand' : 'Import Variants from On-Demand'}
        </DialogTitle>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Grid>
      <DialogContent dividers>
        <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
          <Grid item xs={12} md={8}>
            <TextField
              fullWidth
              value={q}
              onChange={(e) => setQ(e.target.value)}
              label="Search"
              placeholder="Search product, SKU, size..."
            />
          </Grid>
          <Grid item xs={12} md={4} style={{ textAlign: 'right' }}>
            {!singleMode && (
              <Typography variant="body2">
                {selectedSkus.length} selected
              </Typography>
            )}
          </Grid>
        </Grid>

        {loading ? (
          <Grid container justifyContent="center" sx={{ py: 6 }}>
            <CircularProgress />
          </Grid>
        ) : (
          <>
            {filteredGroups.map((g) => (
              <Accordion
                key={g.key}
                expanded={!!expanded[g.key]}
                onChange={() => setExpanded((prev) => ({ ...prev, [g.key]: !prev[g.key] }))}
                sx={{ mb: 1 }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Grid container alignItems="center" spacing={2}>
                    <Grid item>
                      {!singleMode ? (
                        <Checkbox
                          checked={isGroupAllSelected(g)}
                          indeterminate={isGroupSomeSelected(g)}
                          disabled={isGroupFullyDisabled(g)}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleGroup(g)}
                        />
                      ) : (
                        <div style={{ width: 40 }} />
                      )}
                    </Grid>

                    <Grid item xs>
                      <Typography sx={{ fontWeight: 600 }}>{g.title}</Typography>
                      {g.skuGroup ? (
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          SKU Group: {g.skuGroup}
                        </Typography>
                      ) : null}
                    </Grid>

                    <Grid item>
                      <Chip size="small" label={`${(g.variants || []).length} variants`} />
                    </Grid>
                  </Grid>
                </AccordionSummary>
                <AccordionDetails>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell padding="checkbox" />
                        <TableCell>SKU</TableCell>
                        <TableCell>Name</TableCell>
                        <TableCell>Price</TableCell>
                        <TableCell>Currency</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(g.variants || []).map((r) => {
                        const disabled = isDisabledSku(r.sku);
                        return (
                          <TableRow key={r.sku} hover sx={disabled ? { opacity: 0.6 } : {}}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedSkus.includes(r.sku)}
                                disabled={disabled}
                                onChange={() => toggleSku(r.sku)}
                              />
                            </TableCell>
                            <TableCell>{r.sku}</TableCell>
                            <TableCell>
                              <Grid container alignItems="center" spacing={1}>
                                <Grid item>
                                  <Typography sx={{ fontWeight: 500 }}>{r._variantLabel}</Typography>
                                  <Typography variant="body2" sx={{ opacity: 0.8 }}>{r.name}</Typography>
                                </Grid>
                                {disabled ? (
                                  <Grid item>
                                    <Chip size="small" label="Already added" />
                                  </Grid>
                                ) : null}
                              </Grid>
                            </TableCell>
                            <TableCell>{r.price}</TableCell>
                            <TableCell>{r.currency}</TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </AccordionDetails>
              </Accordion>
            ))}

            {!filteredGroups.length && (
              <Grid container justifyContent="center" sx={{ py: 6 }}>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>
                  No items found
                </Typography>
              </Grid>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="text">Cancel</Button>
        <Button
          onClick={handleConfirm}
          disabled={selectedSkus.length === 0}
          variant="contained"
        >
          {singleMode ? 'Create' : 'Add Variants'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default OnDemandCatalogueModal;